import type Quill from 'quill';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { BaseRte } from '#/components/BaseRte/BaseRte';
import type { ListingType } from '#/types/ListingType';

import { ListingRteFullscreenModal } from './ListingRteFullscreenModal';
import { useListingRteWarning } from './useListingRteWarning';

type Props = {
  id: string;
  initialValue: string;
  placeholder?: string;
  intro: ReactNode;
  outro: ReactNode;
  onReady?: (editor: Quill) => void;
  onChange: ((newValue: string) => void) | undefined;
  onPasteText: ((pastedText: string) => void) | undefined;
  showAtsLinkWarnings: boolean;
  disabled: boolean;
  qaId: string;
  hasError?: boolean;
  listingType?: ListingType | undefined;
  labelId?: string;
};

export function ListingRte({
  id,
  initialValue,
  placeholder,
  intro,
  listingType,
  outro,
  onReady,
  onChange,
  onPasteText,
  showAtsLinkWarnings,
  disabled,
  qaId,
  hasError,
  labelId,
}: Props) {
  const [quill, setQuill] = useState<Quill>();

  const warning = useListingRteWarning({
    enabled: Boolean(showAtsLinkWarnings),
    listingType,
    quill,
  });

  const [currentValue, setCurrentValue] = useState(initialValue || '');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const editor = (
    <BaseRte
      features={{
        header: ['h4', 'normal'],
        bold: true,
        italic: true,
        underline: true,
        strike: false,
        link: true,
        list: true,
        align: false,
        indent: true,
        clean: false,
        blockquote: false,
        fullscreen: {
          onToggleFullscreen: setIsFullscreen,
          initialIsFullscreen: isFullscreen,
        },
      }}
      id={id}
      qaId={qaId}
      placeholder={placeholder}
      intro={intro}
      outro={outro}
      initialValue={currentValue}
      onReady={(newInstance) => {
        setQuill(newInstance);
        onReady?.(newInstance);
      }}
      onChange={(newValue) => {
        setCurrentValue(newValue);
        onChange?.(newValue);
      }}
      onPasteText={onPasteText}
      disabled={disabled}
      warning={warning}
      isFullscreen={isFullscreen}
      hasError={hasError}
      labelId={labelId}
    />
  );

  if (isFullscreen)
    return <ListingRteFullscreenModal>{editor}</ListingRteFullscreenModal>;

  return editor;
}
